import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, ArrowLeft, Languages } from "lucide-react";
import { api, mediaUrl } from "@/lib/site";
import { LANGS } from "@/lib/i18n-extra";

export const inp = "w-full border border-midnight/15 bg-white px-3 py-2.5 text-sm text-midnight outline-none focus:border-gold transition-colors";

export const Fld = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] tracking-[0.25em] uppercase text-midnight/60 mb-1.5">{label}</span>
    {children}
  </label>
);

export const BiText = ({ label, value, onChange, area = false, rows = 5, testid }) => {
  const v = value || {};
  const [l, setL] = useState("sk");
  const [busy, setBusy] = useState(false);
  const C = area ? "textarea" : "input";
  const translate = async () => {
    if (!v.sk) return toast.error("Najprv vyplňte slovenský text");
    setBusy(true);
    try {
      const r = await api.post("/admin/translate", { text: v.sk, source: "sk" });
      onChange({ ...v, ...Object.fromEntries(Object.entries(r.data || {}).filter(([, x]) => x)) });
      toast.success("Preložené do všetkých jazykov");
    } catch { toast.error("Preklad zlyhal"); }
    setBusy(false);
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 mb-1.5">
        <span className="text-[10px] tracking-[0.25em] uppercase text-midnight/60 mr-2">{label}</span>
        {LANGS.map(([code, short]) => (
          <button type="button" key={code} onClick={() => setL(code)} data-testid={`${testid}-tab-${code}`}
            className={`text-[10px] px-1.5 py-0.5 border ${l === code ? "bg-midnight text-ivory border-midnight" : v[code] ? "border-gold text-midnight" : "border-midnight/15 text-midnight/40"}`}>{short}</button>
        ))}
        <button type="button" onClick={translate} disabled={busy} data-testid={`${testid}-translate`} className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-gold hover:text-midnight">
          <Languages size={13} /> {busy ? "Prekladám…" : "Preložiť z SK"}
        </button>
      </div>
      <C rows={rows} dir={l === "he" ? "rtl" : "ltr"} className={inp} value={v[l] || ""} data-testid={`${testid}-${l}`} onChange={(e) => onChange({ ...v, [l]: e.target.value })} />
    </div>
  );
};

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/upload", fd);
  return r.data.url;
};

export const UploadBtn = ({ onUploaded, accept = "image/*,.heic,.heif", multiple = false, label = "Nahrať súbor", testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) onUploaded(await uploadFile(f));
      toast.success("Nahrané");
    } catch (e) { toast.error(e.response?.data?.detail || "Nahrávanie zlyhalo"); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={onChange} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testid} className="inline-flex items-center gap-2 border border-dashed border-gold text-midnight px-4 py-2.5 text-xs tracking-[0.15em] uppercase hover:bg-gold/10">
        <Upload size={14} /> {busy ? "Nahrávam…" : label}
      </button>
    </>
  );
};

export const ImageList = ({ images, onChange }) => (
  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="admin-image-list">
    {images.map((src, i) => (
      <div key={src + i} className="relative group aspect-square bg-sand/30">
        <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover" />
        {i === 0 && <span className="absolute top-1 left-1 bg-gold text-midnight text-[9px] px-1.5 py-0.5 uppercase">Hlavná</span>}
        <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 opacity-0 group-hover:opacity-100 bg-midnight/60">
          <button type="button" disabled={i === 0} onClick={() => { const a = [...images]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; onChange(a); }} className="text-ivory p-1"><ArrowLeft size={14} /></button>
          <button type="button" onClick={() => onChange(images.filter((_, k) => k !== i))} className="text-ivory p-1" data-testid={`remove-image-${i}`}><X size={14} /></button>
        </div>
      </div>
    ))}
  </div>
);
